import { store } from 'redux/store';
import { State } from 'redux/initialState';
import { useTranslate } from 'hooks/useTranslate';
import { FC, createContext, useContext, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import errorSwal from 'utils/errorSwal';
import successSwal from 'utils/successSwal';

import { setModal } from 'modules/_shared/redux/modalActions';
import { deleteOperation } from 'modules/transactions/redux/operationActions';

import { rollbackOperationModalInitValue } from '../constants/rollbackOperationModalInitValue';

import { RollbackOperationModalContextType } from '../types/RollbackOperationModalContextType';
import { useGetLastOperationData } from '../hooks/useGetLastOperationData';

const RollbackOperationModalContext =
  createContext<RollbackOperationModalContextType>(
    rollbackOperationModalInitValue
  );

export const RollbackOperationModalProvider: FC<{
  children: React.ReactNode; // eslint-disable-line
  props: any;
}> = ({ children, ...props }) => {
  const dispatch = useDispatch();
  const { t } = useTranslate();

  const actualSociety = useSelector(
    (state: State) => state.society?.actualSociety
  );

  const [confirmationText, setConfirmationDate] = useState<string>('');
  const [isRollBackConfirmed, setIsRollBackConfirmed] =
    useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const { data, isLoading: isLastOperationLoading } = useGetLastOperationData({
    societyId: actualSociety?._id,
  });

  const handleCloseModal = () => {
    dispatch(setModal(null));
  };

  const handleChangeConfirmationText = (value: string) => {
    const confirmationText = value;
    setConfirmationDate(confirmationText);

    const isRollBackConfirmed = confirmationText === t('Delete');
    setIsRollBackConfirmed(isRollBackConfirmed);
  };

  const handleRollbackOperation = async () => {
    try {
      if (!data.operationId || data.isRollbackDisabled) {
        return;
      }

      setIsLoading(true);

      await store.dispatch(deleteOperation(data.operationId));
      setIsLoading(false);

      successSwal(t('OperationRolledBackSuccessfully'));
    } catch (error) {
      errorSwal(t('ErrorRollingBackOperation'));
    } finally {
      handleCloseModal();
    }
  };

  return (
    <RollbackOperationModalContext.Provider
      value={{
        data: {
          confirmationText,
          operationTypeLabel: data.operationTypeLabel,
          date: data.date,
          isRollBackConfirmed,
          isRollbackDisabled: data.isRollbackDisabled,
          isLoading: isLoading || isLastOperationLoading,
        },
        actions: {
          handleCloseModal,
          handleChangeConfirmationText,
          handleRollbackOperation,
        },
      }}
    >
      {children}
    </RollbackOperationModalContext.Provider>
  );
};

export const useRollbackOperationModal = () => {
  const context = useContext(RollbackOperationModalContext);
  if (!context) {
    throw new Error(
      'useRollbackOperationModal must be used within a RollbackOperationModalProvider'
    );
  }
  return context;
};
